import type { PropertyTypeEnum } from '~/core/entities/CRMEnums';

import { propertyTypesSlugByValue } from '~/core/entities/CRMEnums';
import { slugify } from '~~/utils';

export const SHORTS_HOME_PATH = '/shorts/home';

export interface ShortsLocationPath {
	path: string;
	query: Record<string, string>;
}

export interface ShortsLocationPathOptions {
	macroregions: string[];
	cities: string[];
	regions: string[];
	states: string[];
	propertyTypes: PropertyTypeEnum[];
	minAskingPrice?: number | null;
	maxAskingPrice?: number | null;
	minArea?: number | null;
	maxArea?: number | null;
}

/**
 * Constrói o path e query para a home de Shorts a partir dos filtros de localização.
 * Localização e tipo de imóvel vão no path; macroregion e filtros numéricos ficam como query params.
 *
 * Exemplos:
 *   sp-brasil?macroregions=Interior
 *   sao-paulo-sp-brasil
 *   jardins-sao-paulo-sp-brasil
 *   sao-paulo-sp-brasil/apartamento
 */
function buildLocationSlug(
	region?: string,
	city?: string,
	state?: string
): string {
	if (region && city && state)
		return `${slugify(region)}-${slugify(city)}-${slugify(state)}-brasil`;
	if (city && state) return `${slugify(city)}-${slugify(state)}-brasil`;
	if (state) return `${slugify(state)}-brasil`;
	return '';
}

export function buildShortsLocationPath({
	macroregions,
	cities,
	regions,
	states,
	propertyTypes,
	minAskingPrice,
	maxAskingPrice,
	minArea,
	maxArea
}: ShortsLocationPathOptions): ShortsLocationPath {
	const state = states[0];
	const city = cities[0];
	const region = regions[0];
	const macroregion = macroregions[0];
	const [firstPropertyType, ...remainingPropertyTypes] = propertyTypes;

	const query: Record<string, string> = {};

	if (minAskingPrice != null) query.minAskingPrice = String(minAskingPrice);
	if (maxAskingPrice != null) query.maxAskingPrice = String(maxAskingPrice);
	if (minArea != null) query.minArea = String(minArea);
	if (maxArea != null) query.maxArea = String(maxArea);

	const locationSlug = buildLocationSlug(region, city, state);
	const propertyTypeSlug = firstPropertyType
		? propertyTypesSlugByValue[firstPropertyType]
		: '';

	const path = [SHORTS_HOME_PATH, locationSlug, propertyTypeSlug]
		.filter(Boolean)
		.join('/');

	if (remainingPropertyTypes.length) {
		query.propertyTypes = remainingPropertyTypes
			.map(t => propertyTypesSlugByValue[t])
			.join(',');
	}

	if (macroregion) {
		query.macroregions = macroregion;
	}

	return { path, query };
}

export async function navigateToShortsLocationPath(
	options: ShortsLocationPathOptions
) {
	const { path, query } = buildShortsLocationPath(options);
	// Runs after the store's query sync, whose query-only replace would cancel this path change.
	await nextTick();
	return navigateTo({ path, query }, { replace: true });
}
